import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import ELK from 'elkjs/lib/elk.bundled';
import { useMemo } from 'react';
import { connectorNodeSize, nodeSpacing, partnerNodeSize } from '../config/constants';
import type { FamilyTreeEdgeType, FamilyTreeNode } from '../interfaces';
import { applyStoredNodePositions, enrichFamilyTreeNodesWithElkNodes, optimizeNodePositions } from '../utils';

const elk = new ELK();

// See the following documentation of Elk to see descriptions of these options and all other existing options:
// - https://www.eclipse.org/elk/reference/algorithms.html
// - https://www.eclipse.org/elk/reference/options.html
const elkOptions = {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.spacing.nodeNode': `${nodeSpacing.x}`,
    'elk.layered.spacing.nodeNodeBetweenLayers': `${nodeSpacing.y}`,
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.layering.strategy': 'NETWORK_SIMPLEX',
    'org.eclipse.elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
};

export interface IUseFamiliesTreeElkLayoutResult {
    enrichedEdges: FamilyTreeEdgeType[];
    enrichedNodes: FamilyTreeNode[];
}

export function useFamiliesTreeElkLayout(
    nodes: FamilyTreeNode[],
    edges: FamilyTreeEdgeType[],
    autoPosition?: boolean,
): UseQueryResult<IUseFamiliesTreeElkLayoutResult, AxiosError> {
    const queryKey = useMemo(() => ['FAMILY_TREE', 'ITEM', nodes, edges], [nodes, edges]);

    return useQuery({
        queryKey,
        queryFn: () =>
            elk.layout({
                id: 'root',
                layoutOptions: elkOptions,
                children: nodes.map((node) => ({
                    ...node,
                    targetPosition: node.data.isConnector ? 'right' : 'top',
                    sourcePosition: node.data.isConnector ? 'left' : 'bottom',
                    width: node.data.isConnector ? connectorNodeSize.width : partnerNodeSize.width,
                    height: node.data.isConnector ? connectorNodeSize.height : partnerNodeSize.height,
                })),
                edges: edges.map((edge) => ({
                    id: edge.id,
                    sources: [edge.source],
                    targets: [edge.target],
                })),
            }),
        enabled: nodes.length > 1,
        select: (elkLayout) => {
            const { enrichedEdges, enrichedNodes } = enrichFamilyTreeNodesWithElkNodes(nodes, elkLayout.children ?? [], edges, elkLayout.edges ?? []);
            const optimizedNodes = optimizeNodePositions(enrichedNodes, enrichedEdges);

            return {
                enrichedEdges,
                enrichedNodes: applyStoredNodePositions(nodes, optimizedNodes, autoPosition),
            };
        },
    });
}
